import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";import {
  LayoutDashboard, Users, GraduationCap, BookOpen, CreditCard, ShoppingCart,
  Tag, Megaphone, Trophy, BarChart3, ShieldAlert, History, Settings as Cog,
  Headphones, FileBadge, Sparkles, Activity, Globe, LogOut, AlertTriangle,
  Layers, Wrench, MousePointerClick, Calendar, UserPlus,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, setToken, clearToken, setStoredUser } from "../lib/api";
import { toast } from "sonner";

const SECTIONS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, exact: true, testid: "admin-side-overview" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "admin-side-users" },
  { to: "/admin/instructors", label: "Instructors", icon: GraduationCap, testid: "admin-side-instructors" },
  { to: "/admin/courses", label: "Courses", icon: BookOpen, testid: "admin-side-courses" },
  { to: "/admin/live-events", label: "Live Events", icon: Calendar, testid: "admin-side-live-events" },
  { to: "/admin/leads", label: "Leads", icon: UserPlus, testid: "admin-side-leads" },
  { to: "/admin/plans", label: "Plans", icon: CreditCard, testid: "admin-side-plans" },
  { to: "/admin/upsells", label: "Upsells", icon: ShoppingCart, testid: "admin-side-upsells" },
  { to: "/admin/promotions", label: "Promotions", icon: Tag, testid: "admin-side-promotions" },
  { to: "/admin/sponsors", label: "Sponsors", icon: Megaphone, testid: "admin-side-sponsors" },
  { to: "/admin/affiliate-partners", label: "Affiliate Partners", icon: Wrench, testid: "admin-side-affiliate-partners" },
  { to: "/admin/cta-analytics", label: "CTA Analytics", icon: MousePointerClick, testid: "admin-side-cta-analytics" },
  { to: "/admin/audiences", label: "Audiences", icon: Layers, testid: "admin-side-audiences" },
  { to: "/admin/newsletter", label: "Newsletter", icon: Headphones, testid: "admin-side-newsletter" },
  { to: "/admin/gamification", label: "Gamification", icon: Trophy, testid: "admin-side-gamification" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testid: "admin-side-analytics" },
  { to: "/admin/security", label: "Security", icon: ShieldAlert, testid: "admin-side-security" },
  { to: "/admin/audit", label: "Audit Log", icon: History, testid: "admin-side-audit" },
  { to: "/admin/settings", label: "Settings", icon: Cog, testid: "admin-side-settings" },
];

const EXTRAS = [
  { to: "/admin/assessments", label: "Assessments", icon: Sparkles, testid: "admin-side-assessments" },
  { to: "/admin/certificates", label: "Certificates", icon: FileBadge, testid: "admin-side-certificates" },
  { to: "/admin/support", label: "Support", icon: Headphones, testid: "admin-side-support" },
  { to: "/admin/emergent-sync", label: "Emergent Sync", icon: Activity, testid: "admin-side-emergent" },
];

export function ImpersonationBanner() {
  const { user } = useAuth();
  if (!user?.is_impersonated) return null;

  const stopImpersonation = async () => {
    try {
      await api.post("/admin/impersonate/stop");
    } catch (e) { /* even on failure, restore admin token below */ }
    const backup = localStorage.getItem("fc_admin_token_backup");
    const backupUser = localStorage.getItem("fc_admin_user_backup");
    if (backup) {
      setToken(backup);
      if (backupUser) {
        try { setStoredUser(JSON.parse(backupUser)); } catch {}
      }
      localStorage.removeItem("fc_admin_token_backup");
      localStorage.removeItem("fc_admin_user_backup");
      window.location.href = "/admin/users";
    } else {
      clearToken();
      window.location.href = "/login";
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-fc-red text-white px-4 py-2 flex items-center justify-between gap-3 text-xs font-bold tracking-wider shadow-lg" data-testid="impersonation-banner">
      <span className="flex items-center gap-2">
        <AlertTriangle size={14} />
        IMPERSONATING: <span className="font-mono">{user.public_alias}</span> ({user.email})
      </span>
      <button onClick={stopImpersonation} className="bg-white text-fc-red px-3 py-1 font-black tracking-wider hover:bg-fc-light-gray" data-testid="stop-impersonation">
        ← RETURN TO ADMIN
      </button>
    </div>
  );
}

export default function AdminLayout({ children, currentSection }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  if (user && user.role !== "super_admin") {
    toast.error("Admin access required");
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-fc-navy flex" data-surface="light" data-testid="admin-layout">
      <aside className="hidden lg:flex w-64 flex-shrink-0 flex-col bg-[#060A24] border-r border-white/10 sticky top-0 h-screen overflow-y-auto" data-testid="admin-sidebar">
        <Link to="/" className="flex items-center gap-3 h-20 px-6 border-b border-white/10 flex-shrink-0">
          <img src="/brand/freddy-symbol.webp" alt="Freddy" className="h-9 w-auto" />
          <div>
            <p className="text-white font-black tracking-[0.18em] text-[11px] leading-tight">FREDDY ADMIN</p>
            <p className="text-fc-red text-[9px] tracking-[0.18em] font-bold">SUPER ADMIN</p>
          </div>
        </Link>
        <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-1 no-scrollbar">
          {SECTIONS.map((it) => {
            const active = it.exact ? pathname === it.to : (pathname === it.to || pathname.startsWith(it.to + "/"));
            const Icon = it.icon;
            return (
              <Link key={it.to} to={it.to} className={`flex items-center gap-3 px-3 py-2 text-sm font-semibold rounded-sm transition-colors ${active ? "bg-fc-red/15 text-fc-red border-l-2 border-fc-red" : "text-white/75 hover:bg-white/5 hover:text-white"}`} data-testid={it.testid}>
                <Icon size={14} /> {it.label}
              </Link>
            );
          })}
          <div className="px-3 mt-5 mb-2 text-[10px] tracking-[0.22em] uppercase font-bold text-white/40">EXTRAS</div>
          {EXTRAS.map((it) => {
            const Icon = it.icon;
            const active = pathname === it.to;
            return (
              <Link key={it.to} to={it.to} className={`flex items-center gap-3 px-3 py-2 text-sm font-semibold rounded-sm transition-colors ${active ? "bg-fc-red/15 text-fc-red border-l-2 border-fc-red" : "text-white/75 hover:bg-white/5 hover:text-white"}`} data-testid={it.testid}>
                <Icon size={14} /> {it.label}
              </Link>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-white/10 flex-shrink-0">
          <button onClick={() => navigate("/dashboard")} className="w-full bg-fc-red text-white px-3 py-2.5 text-xs tracking-[0.18em] uppercase font-black hover:opacity-90 flex items-center justify-center gap-2" data-testid="return-to-learner">
            <Globe size={12} /> RETURN TO LEARNER
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 bg-[#F8F9FB] text-gray-900">{children}</main>
    </div>
  );
}

export function AdminPageHeader({ title, overline = "ADMIN", icon, children }) {
  const Icon = icon;
  return (
    <div className="h-20 px-6 lg:px-10 flex items-center justify-between border-b border-gray-200 bg-white/70 backdrop-blur sticky top-0 z-30">
      <div>
        <p className="fc-overline text-[10px]">{overline}</p>
        <h1 className="text-lg sm:text-xl font-black tracking-tight flex items-center gap-2">
          {Icon && <Icon size={18} />} {title}
        </h1>
      </div>
      {children}
    </div>
  );
}
